import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft, Users, User as UserIcon } from "lucide-react";
import { GroupOfPeopleIcon } from "../../../../shared/ui/icons/SubjectKindIcon";

import Button from "../../../../shared/ui/button/Button";
import Input from "../../../../shared/ui/form/input/InputField";
import Label from "../../../../shared/ui/form/Label";
import { useTherapist } from "../../../../context/useTherapist";
import { useCenter, useCenterMembers, useHandOverAndChangeRoles, useHandOverAndRemove, useMemberCaseload, CenterCouple, CenterGroup, CenterMember, CenterPatient, CenterRole } from "../../../center";
import CenterAdminShell from "../components/CenterAdminShell";
import { centerGroupLabel } from "../centerGroupLabel";

const SELECT_CLASS =
    "h-10 w-full rounded-lg border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700 sm:w-56";

const ERRORS: Record<string, string> = {
    handover_incomplete: "center.departure.errorChanged",
    couple_not_fully_in_center: "center.departure.errorCoupleOutside",
    group_not_fully_in_center: "center.departure.errorGroupOutside",
    handover_splits_a_patient: "center.departure.errorSplit",
    // One unit's booking lands on a slot its destination has taken. Units
    // before it may already have moved: the handover is not undone.
    handover_slot_taken: "center.departure.errorSlotTaken",
    therapist_not_in_center: "center.departure.errorDestination",
    cannot_remove_yourself: "center.departure.errorYourself",
    member_has_patients: "center.departure.errorChanged",
    last_administrator: "center.team.rolesErrorLastAdmin",
    cannot_change_own_admin: "center.team.rolesErrorOwnAdmin",
};

interface Props {
    /**
     * `leave` takes them out of the centre once everything is handed over;
     * `stopPractising` keeps them in it, without the therapist role.
     */
    mode?: "leave" | "stopPractising";
}

const fullName = (person: { first_name: string; last_name: string }) =>
    `${person.first_name} ${person.last_name}`;

const coupleName = (couple: CenterCouple) =>
    couple.display_name || couple.members.map(fullName).join(" · ");

/** Its given name, or its size — never who is in it (see `centerGroupLabel`). */
const groupName = (t: TFunction, group: CenterGroup) =>
    centerGroupLabel(t, group.display_name, group.members.length);

/**
 * Taking somebody out of the centre, with their caseload handed over first.
 *
 * Every patient the centre holds that they treat goes to somebody who stays —
 * individuals one by one, couples and groups as the unit they are treated as. Nothing
 * leaves until everything has a destination, and the server checks the whole
 * plan again before moving anybody.
 *
 * Names and nothing else, like every other centre screen: choosing who takes
 * over a caseload does not require reading a word of it.
 */
export default function CenterMemberDeparturePage({ mode = "leave" }: Props) {
    const stopping = mode === "stopPractising";
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { membershipId } = useParams<{ membershipId: string }>();
    const { therapist } = useTherapist();
    const { center, loading } = useCenter();
    const { members, loading: membersLoading } = useCenterMembers(center?.id);
    const { caseload, loading: caseloadLoading, failed } = useMemberCaseload(
        center?.id,
        membershipId
    );
    const handOver = useHandOverAndRemove(center?.id);
    const handOverAndChange = useHandOverAndChangeRoles(center?.id);
    const pending = handOver.isPending || handOverAndChange.isPending;

    const [patientTargets, setPatientTargets] = useState<Record<string, string>>({});
    const [coupleTargets, setCoupleTargets] = useState<Record<string, string>>({});
    const [groupTargets, setGroupTargets] = useState<Record<string, string>>({});
    const [reason, setReason] = useState("");
    const [error, setError] = useState<string | null>(null);

    if (loading || !center || membersLoading || caseloadLoading) {
        return <p className="p-6 text-sm text-gray-500">{t("common.loading")}</p>;
    }

    const leaving = members.find((member) => member.membership_id === membershipId);
    const back = (
        <Link
            to="/center/team"
            className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
        >
            <ArrowLeft size={16} aria-hidden="true" />
            {t("center.departure.back")}
        </Link>
    );

    if (!leaving || failed || !caseload) {
        return (
            <CenterAdminShell center={center}>
                <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                    {back}
                    <p className="text-sm text-gray-500">{t("center.departure.notFound")}</p>
                </section>
            </CenterAdminShell>
        );
    }

    // Leaving is refused for oneself; stopping treating is not — an
    // administrator may hand over their own patients and keep running the place.
    const isYourself = !stopping && leaving.therapist_id === therapist?.id;
    const rolesAfter: CenterRole[] = leaving.roles.filter((role) => role !== "therapist");
    const destinations: CenterMember[] = members.filter(
        (member) => member.is_practising_therapist && member.therapist_id !== leaving.therapist_id
    );
    const groups = caseload.groups ?? [];
    const movable = caseload.couples.filter((couple) => couple.fully_in_center);
    const movableGroups = groups.filter((group) => group.fully_in_center);
    const blocked = [
        ...caseload.couples.filter((couple) => !couple.fully_in_center),
        ...groups.filter((group) => !group.fully_in_center),
    ];
    const isEmpty =
        caseload.individuals.length === 0 && caseload.couples.length === 0 && groups.length === 0;
    const everyoneHasSomewhere =
        caseload.individuals.every((p) => patientTargets[p.patient_id]) &&
        movable.every((c) => coupleTargets[c.couple_id]) &&
        movableGroups.every((g) => groupTargets[g.group_id]);
    const canLeave =
        !isYourself &&
        (!stopping || rolesAfter.length > 0) &&
        blocked.length === 0 &&
        everyoneHasSomewhere &&
        (isEmpty || destinations.length > 0) &&
        !pending;

    const assignAll = (therapistId: string) => {
        setPatientTargets(
            Object.fromEntries(caseload.individuals.map((p) => [p.patient_id, therapistId]))
        );
        setCoupleTargets(Object.fromEntries(movable.map((c) => [c.couple_id, therapistId])));
        setGroupTargets(Object.fromEntries(movableGroups.map((g) => [g.group_id, therapistId])));
    };

    const handleLeave = async () => {
        setError(null);
        try {
            const handover = {
                patients: caseload.individuals.map((p) => ({
                    patient_id: p.patient_id,
                    to_therapist_id: patientTargets[p.patient_id],
                })),
                couples: movable.map((c) => ({
                    couple_id: c.couple_id,
                    to_therapist_id: coupleTargets[c.couple_id],
                })),
                groups: movableGroups.map((g) => ({
                    group_id: g.group_id,
                    to_therapist_id: groupTargets[g.group_id],
                })),
                reason: reason.trim() || undefined,
            };
            if (stopping) {
                await handOverAndChange.mutateAsync({
                    membershipId: leaving.membership_id,
                    handover,
                    roles: rolesAfter,
                });
            } else {
                await handOver.mutateAsync({ membershipId: leaving.membership_id, handover });
            }
            navigate("/center/team");
        } catch (err: unknown) {
            const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data
                ?.detail;
            setError(t((detail && ERRORS[detail]) || "center.departure.errorGeneric"));
        }
    };

    const destinationSelect = (
        id: string,
        value: string,
        onChange: (therapistId: string) => void,
        label: string
    ) => (
        <select
            id={id}
            aria-label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={SELECT_CLASS}
        >
            <option value="">{t("center.departure.choose")}</option>
            {destinations.map((member) => (
                <option key={member.therapist_id} value={member.therapist_id}>
                    {fullName(member)}
                </option>
            ))}
        </select>
    );

    const row = (
        key: string,
        icon: React.ReactNode,
        name: string,
        detail: string | null,
        control: React.ReactNode
    ) => (
        <li
            key={key}
            className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
        >
            <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400">
                    {icon}
                </span>
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-content-primary">{name}</p>
                    {detail && <p className="text-xs text-gray-500 dark:text-gray-400">{detail}</p>}
                </div>
            </div>
            {control}
        </li>
    );

    return (
        <CenterAdminShell center={center}>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                {back}
                <h2 className="mb-1 text-lg font-semibold text-content-primary">
                    {t(stopping ? "center.stopPractising.title" : "center.departure.title", {
                        name: fullName(leaving),
                    })}
                </h2>
                <p className="mb-5 text-sm text-gray-500 dark:text-gray-400">
                    {t(stopping ? "center.stopPractising.consequences" : "center.departure.consequences")}
                </p>

                {stopping && rolesAfter.length === 0 && (
                    <p role="alert" className="mb-5 text-sm text-red-600 dark:text-red-400">
                        {t("center.stopPractising.needsAnotherRole")}
                    </p>
                )}

                {isYourself && (
                    <p role="alert" className="mb-5 text-sm text-red-600 dark:text-red-400">
                        {t("center.departure.errorYourself")}
                    </p>
                )}

                {!isYourself && isEmpty && (
                    <p className="mb-5 rounded-xl bg-gray-50 px-4 py-3 text-sm text-content-primary dark:bg-white/[0.02]">
                        {t(
                            stopping
                                ? "center.stopPractising.nothingToHandOver"
                                : "center.departure.nothingToHandOver"
                        )}
                    </p>
                )}

                {!isYourself && !isEmpty && (
                    <>
                        <h3 className="mb-1 text-sm font-semibold text-content-primary">
                            {t("center.departure.handOverTitle")}
                        </h3>
                        <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                            {t("center.departure.handOverHint")}
                        </p>

                        {destinations.length === 0 ? (
                            <p className="mb-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                                {t("center.departure.noDestinations")}
                            </p>
                        ) : (
                            <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                                <Label htmlFor="assign-all" className="mb-0">
                                    {t("center.departure.assignAll")}
                                </Label>
                                {destinationSelect(
                                    "assign-all",
                                    "",
                                    assignAll,
                                    t("center.departure.assignAll")
                                )}
                            </div>
                        )}

                        {caseload.individuals.length > 0 && (
                            <>
                                <h4 className="mt-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {t("center.departure.individuals", {
                                        count: caseload.individuals.length,
                                    })}
                                </h4>
                                <ul className="mb-4 divide-y divide-gray-100 dark:divide-gray-800">
                                    {caseload.individuals.map((patient: CenterPatient) =>
                                        row(
                                            patient.patient_id,
                                            <UserIcon size={16} aria-hidden="true" />,
                                            fullName(patient),
                                            null,
                                            destinationSelect(
                                                `to-${patient.patient_id}`,
                                                patientTargets[patient.patient_id] ?? "",
                                                (id) =>
                                                    setPatientTargets((current) => ({
                                                        ...current,
                                                        [patient.patient_id]: id,
                                                    })),
                                                t("center.departure.destinationFor", {
                                                    name: fullName(patient),
                                                })
                                            )
                                        )
                                    )}
                                </ul>
                            </>
                        )}

                        {caseload.couples.length > 0 && (
                            <>
                                <h4 className="mt-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {t("center.departure.couples", {
                                        count: caseload.couples.length,
                                    })}
                                </h4>
                                <ul className="mb-4 divide-y divide-gray-100 dark:divide-gray-800">
                                    {caseload.couples.map((couple) =>
                                        row(
                                            couple.couple_id,
                                            <Users size={16} aria-hidden="true" />,
                                            coupleName(couple),
                                            couple.fully_in_center
                                                ? t("center.departure.coupleMovesTogether")
                                                : t("center.departure.coupleOutside"),
                                            couple.fully_in_center ? (
                                                destinationSelect(
                                                    `to-${couple.couple_id}`,
                                                    coupleTargets[couple.couple_id] ?? "",
                                                    (id) =>
                                                        setCoupleTargets((current) => ({
                                                            ...current,
                                                            [couple.couple_id]: id,
                                                        })),
                                                    t("center.departure.destinationFor", {
                                                        name: coupleName(couple),
                                                    })
                                                )
                                            ) : (
                                                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                                                    {t("center.departure.coupleBlocked")}
                                                </span>
                                            )
                                        )
                                    )}
                                </ul>
                            </>
                        )}

                        {groups.length > 0 && (
                            <>
                                <h4 className="mt-2 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {t("center.departure.groups", { count: groups.length })}
                                </h4>
                                <ul className="mb-4 divide-y divide-gray-100 dark:divide-gray-800">
                                    {groups.map((group) =>
                                        row(
                                            group.group_id,
                                            <GroupOfPeopleIcon size={16} aria-hidden="true" />,
                                            groupName(t, group),
                                            group.fully_in_center
                                                ? t("center.departure.groupMovesTogether", {
                                                      count: group.members.length,
                                                  })
                                                : t("center.departure.groupOutside"),
                                            group.fully_in_center ? (
                                                destinationSelect(
                                                    `to-${group.group_id}`,
                                                    groupTargets[group.group_id] ?? "",
                                                    (id) =>
                                                        setGroupTargets((current) => ({
                                                            ...current,
                                                            [group.group_id]: id,
                                                        })),
                                                    t("center.departure.destinationFor", {
                                                        name: groupName(t, group),
                                                    })
                                                )
                                            ) : (
                                                <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                                                    {t("center.departure.groupBlocked")}
                                                </span>
                                            )
                                        )
                                    )}
                                </ul>
                            </>
                        )}

                        <div className="mb-5 max-w-lg">
                            <Label htmlFor="departure-reason">{t("center.departure.reason")}</Label>
                            <Input
                                id="departure-reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                        </div>
                    </>
                )}

                {error && (
                    <p role="alert" className="mb-4 text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}

                <div className="flex flex-wrap gap-3">
                    <Button
                        onClick={handleLeave}
                        disabled={!canLeave}
                        loading={pending}
                        variant={stopping ? "primary" : "destructive"}
                    >
                        {stopping
                            ? isEmpty
                                ? t("center.stopPractising.confirm")
                                : t("center.stopPractising.confirmWithHandOver")
                            : isEmpty
                              ? t("center.departure.confirm")
                              : t("center.departure.confirmWithHandOver")}
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/center/team")}>
                        {t("common.cancel")}
                    </Button>
                </div>
            </section>
        </CenterAdminShell>
    );
}
